import {
  Abilities,
  AbilityId,
  Actor,
  Falling,
  InputLocked,
  PlayerTag,
  Stunned,
  Transform,
} from '@/components'
import type { SystemFn } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { Events } from '@/game/events'
import { EventsResource } from '@/game/resources'
import {
  InputResource,
  type InputFrame,
  type InputManager,
  type PlayerInput,
} from '@/input/InputManager'
import { INPUT_BUFFER_SECONDS } from '@/input/bindings'

const BUFFERED: Array<[number, keyof PlayerInput]> = [
  [AbilityId.Push, 'push'],
  [AbilityId.Dash, 'dash'],
  [AbilityId.Wave, 'wave'],
  [AbilityId.Vortex, 'vortex'],
]

/**
 * Turns raw devices into per-actor intent.
 *
 * The devices are polled once per rendered frame and the frame is applied on
 * every fixed step. Presses wait in the frame until a step consumes them
 * (`consumePresses`), so a press between two steps is not lost and one frame
 * with two steps does not apply it twice; the ability buffer then keeps it
 * warm through a lockout.
 */
/**
 * Polls the devices and publishes the frame.
 *
 * Split from applying it so the AI can run in between and overwrite the slots
 * it controls. A bot therefore plays through exactly the same pipeline as a
 * human — same buffer, same lockouts, same ability rules — instead of reaching
 * into the simulation behind the input layer's back.
 */
export function createInputPollSystem(manager: InputManager): SystemFn {
  return ({ world }) => {
    world.setResource(InputResource, manager.poll())
  }
}

/** Writes whatever the input frame currently says onto the actors. */
export function inputApplySystem({ world }: { world: World }): void {
  const frame = world.tryResource(InputResource)
  if (frame) applyPlayerInput(world, frame)
}

/** Copies a normalised input frame onto the actors. */
function applyPlayerInput(world: World, frame: InputFrame): void {
  for (const e of world.query([PlayerTag, Actor, Abilities]).entities()) {
    const tag = world.read(e, PlayerTag)
    const actor = world.read(e, Actor)
    const abilities = world.read(e, Abilities)
    const input = frame.players[tag.player]
    if (!input) continue

    const locked =
      world.has(e, InputLocked) || world.has(e, Stunned) || world.has(e, Falling)

    if (locked) {
      actor.thrustX = 0
      actor.thrustZ = 0
      abilities.buffer.fill(0)
      continue
    }

    actor.thrustX = input.moveX
    actor.thrustZ = input.moveZ

    for (const [ability, key] of BUFFERED) {
      if (!(input[key] as { pressed: boolean }).pressed) continue

      // The buffer only arms when the ability is actually about to come back.
      // Held open regardless, it turns into a mashing amplifier: presses made
      // seconds early fire on their own the instant the charge lands, at a
      // moment the player never chose. A press with nothing behind it is not
      // silently dropped either — it reports itself, so the game visibly
      // answers every button.
      const ready =
        abilities.charges[ability] > 0 ||
        abilities.recharge[ability] <= INPUT_BUFFER_SECONDS

      if (ready) {
        abilities.buffer[ability] = INPUT_BUFFER_SECONDS
      } else {
        const tr = world.get(e, Transform)
        world.resource(EventsResource).emit(Events.Ability, {
          entity: e,
          player: tag.player,
          ability,
          phase: 'fail',
          x: tr?.x ?? 0,
          z: tr?.z ?? 0,
          angle: tr?.angle ?? 0,
        })
      }
    }
    // Brace is a hold: the ability system reads the live button state instead,
    // because a buffered press cannot express "still held".
  }
}
